import React, { useEffect, useMemo, useState } from 'react';
import { AxiosError } from 'axios';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { ModalCard, ConfigCard } from '@/components/ui';
import { ConfigField } from '@/components/ui';
import { ErrorModal } from '@/components/error/ErrorModal';
import { failuresApi } from '@/services/api/failures';
import { filterOptionsService } from '@/services/api/filterOptions';
import { turbinesApi } from '@/services/api';
import { vblApi } from '@/services/api/vblApi';
import { turbineWorkspaceMappingsApi } from '@/services/api/turbineWorkspaceMappings';
import { workspacesApi } from '@/services/api/workspacesApi';
import { useAuth } from '@/hooks/useAuth';
import type { TurbineToForecast } from '@/types/vbl';
import type { NewFailurePayload, UpdatedFailurePayload } from '@/types/failure';
import type { TurbineWorkspaceMapping } from '@/types/turbineWorkspaceMapping';

const ZERO_UUID = '00000000-0000-0000-0000-000000000000';

interface VblForecastEditModalProps {
  isOpen: boolean;
  mode: 'create' | 'edit';
  initial?: TurbineToForecast;
  onClose: () => void;
  onSaved: () => void;
  onClosedOff?: (failureId: string) => void;
}

interface FormState {
  site_id: string;
  turbine_id: string;
  major_component_id: string;
  component_id: string;
  failure_mode_name: string;
  detected_date: string;
  latest_inspection_severity_id: string;
  latest_diagnostic_severity_id: string;
  case_id: string;
  down_date: string;
  actual_rts_date: string;
  outcome_id: string;
  workspace_id: string;
}

const EMPTY_FORM: FormState = {
  site_id: '',
  turbine_id: '',
  major_component_id: '',
  component_id: '',
  failure_mode_name: '',
  detected_date: '',
  latest_inspection_severity_id: '',
  latest_diagnostic_severity_id: '',
  case_id: '',
  down_date: '',
  actual_rts_date: '',
  outcome_id: '',
  workspace_id: '',
};

// Mirrors useVblForecastCalculation.findBestComponentMapping priority:
// 1. major + sub match, 2. sub-component-only match, 3. major-only match.
const findBestMappingForCombo = (
  mappings: TurbineWorkspaceMapping[],
  turbineID: string,
  majorComponentID: string,
  componentID: string
): TurbineWorkspaceMapping | undefined => {
  const isValid = (v: unknown): boolean =>
    v !== null && v !== undefined && String(v).trim() !== '' && v !== ZERO_UUID;
  const turbineMappings = mappings.filter((m) => m.turbine_id === turbineID);
  if (turbineMappings.length === 0) return undefined;
  const majorAndSub = turbineMappings.find(
    (m) =>
      isValid(m.major_component_id) &&
      isValid(m.component_id) &&
      isValid(majorComponentID) &&
      isValid(componentID) &&
      m.major_component_id === majorComponentID &&
      m.component_id === componentID,
  );
  if (majorAndSub) return majorAndSub;
  const subOnly = turbineMappings.find(
    (m) => isValid(m.component_id) && isValid(componentID) && m.component_id === componentID,
  );
  if (subOnly) return subOnly;
  const majorOnly = turbineMappings.find(
    (m) =>
      isValid(m.major_component_id) &&
      !isValid(m.component_id) &&
      isValid(majorComponentID) &&
      m.major_component_id === majorComponentID,
  );
  return majorOnly;
};

const isoToDateInput = (iso?: string): string => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  // Avoid timezone drift by formatting against UTC date parts.
  return d.toISOString().slice(0, 10);
};

const dateInputToISO = (value: string): string => {
  if (!value) return '0001-01-01T00:00:00Z';
  return new Date(`${value}T00:00:00Z`).toISOString();
};

export const VblForecastEditModal: React.FC<VblForecastEditModalProps> = ({
  isOpen,
  mode,
  initial,
  onClose,
  onSaved,
  onClosedOff,
}) => {
  const queryClient = useQueryClient();
  const { user } = useAuth();
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [apiError, setApiError] = useState<string | null>(null);

  const filterOptionsQuery = useQuery({
    queryKey: ['filterOptions'],
    queryFn: () => filterOptionsService.getFilterOptions(),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });
  const filterMappingsQuery = useQuery({
    queryKey: ['filterMappings'],
    queryFn: () => filterOptionsService.getFilterMappings(),
    enabled: isOpen,
    staleTime: 10 * 60 * 1000,
  });

  const turbinesForSiteQuery = useQuery({
    queryKey: ['turbinesForSite', form.site_id],
    queryFn: () =>
      turbinesApi.getTurbines({ page: 1, size: 1000, site_id: form.site_id }),
    enabled: isOpen && form.site_id !== '',
    staleTime: 5 * 60 * 1000,
  });

  const vblWorkspacesQuery = useQuery({
    queryKey: ['workspaces', 'vbl'],
    queryFn: () => workspacesApi.list({ page: 1, size: 1000, workspaceType: 'vbl' }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  const turbineWorkspaceMappingsQuery = useQuery({
    queryKey: ['turbine-workspace-mappings'],
    queryFn: () => turbineWorkspaceMappingsApi.getMappings({ page: 1, size: 10000 }),
    enabled: isOpen,
    staleTime: 5 * 60 * 1000,
  });

  // Pre-populate form when modal opens.
  useEffect(() => {
    if (!isOpen) return;
    if (mode === 'edit' && initial) {
      const mappings = filterMappingsQuery.data;
      const inspectionName = initial.inspection_severity ?? '';
      const diagnosticName = initial.diagnostic_severity ?? '';
      const existingMapping = findBestMappingForCombo(
        turbineWorkspaceMappingsQuery.data?.mappings ?? [],
        initial.turbine_id ?? '',
        initial.major_component_id ?? '',
        initial.component_id ?? '',
      );
      setForm({
        site_id: initial.site_id ?? '',
        turbine_id: initial.turbine_id ?? '',
        major_component_id: initial.major_component_id ?? '',
        component_id: initial.component_id ?? '',
        failure_mode_name: initial.failure_mode_name ?? '',
        detected_date: isoToDateInput(initial.detected_date),
        latest_inspection_severity_id:
          inspectionName && mappings ? mappings.severityName[inspectionName] ?? '' : '',
        latest_diagnostic_severity_id:
          diagnosticName && mappings ? mappings.severityName[diagnosticName] ?? '' : '',
        case_id: initial.case_id != null ? String(initial.case_id) : '',
        down_date: '',
        actual_rts_date: '',
        outcome_id: initial.outcome_id ?? '',
        workspace_id: existingMapping?.workspace_id ?? '',
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors({});
    setApiError(null);
  }, [isOpen, mode, initial, filterMappingsQuery.data, turbineWorkspaceMappingsQuery.data]);

  const fields: ConfigField[] = useMemo(() => {
    const filterOptions = filterOptionsQuery.data;
    const mappings = filterMappingsQuery.data;

    const siteOptions = (filterOptions?.sites ?? []).map((name) => ({
      value: mappings?.sites[name] ?? '',
      label: name,
    }));
    const turbineOptions = (turbinesForSiteQuery.data?.turbines ?? []).map((t) => ({
      value: t.turbine_id,
      label: t.turbine_name,
    }));
    const majorComponentOptions = (filterOptions?.componentsMajor ?? []).map((name) => ({
      value: mappings?.majorComponents[name] ?? '',
      label: name,
    }));
    const subComponentOptions = (filterOptions?.componentsMinor ?? []).map((name) => ({
      value: mappings?.components[name] ?? '',
      label: name,
    }));
    const failureModeOptions = (filterOptions?.failureModes ?? []).map((name) => ({
      value: name,
      label: name,
    }));
    const severityOptions = (filterOptions?.severityName ?? []).map((name) => ({
      value: mappings?.severityName[name] ?? '',
      label: name,
    }));
    const outcomeOptions = (filterOptions?.outcomeName ?? []).map((name) => ({
      value: mappings?.outcomeName[name] ?? '',
      label: name,
    }));
    const workspaceOptions = (vblWorkspacesQuery.data?.workspaces ?? []).map((w) => ({
      value: w.id,
      label: w.name,
    }));

    return [
      { id: '__section_required', label: 'Required', type: 'section', value: '' },
      {
        id: 'site_id',
        label: 'Site',
        type: 'select',
        value: form.site_id,
        required: true,
        options: siteOptions,
        placeholder: 'Select a site',
        disabled: mode === 'edit',
      },
      {
        id: 'turbine_id',
        label: 'Turbine',
        type: 'select',
        value: form.turbine_id,
        required: true,
        options: turbineOptions,
        placeholder: form.site_id ? 'Select a turbine' : 'Select a site first',
        disabled: !form.site_id || mode === 'edit',
      },
      {
        id: 'major_component_id',
        label: 'Major Component',
        type: 'select',
        value: form.major_component_id,
        required: true,
        options: majorComponentOptions,
        placeholder: 'Select a major component',
        disabled: mode === 'edit',
      },
      {
        id: 'component_id',
        label: 'Sub-Component',
        type: 'select',
        value: form.component_id,
        required: true,
        options: subComponentOptions,
        placeholder: 'Select a sub-component',
        disabled: mode === 'edit',
      },
      {
        id: 'failure_mode_name',
        label: 'Failure Mode',
        type: 'select',
        value: form.failure_mode_name,
        required: true,
        options: failureModeOptions,
        placeholder: 'Select a failure mode',
        disabled: mode === 'edit',
      },
      {
        id: 'latest_diagnostic_severity_id',
        label: 'Last Diagnostic Severity',
        type: 'select',
        value: form.latest_diagnostic_severity_id,
        required: true,
        options: severityOptions,
        placeholder: 'Select a severity',
        validation: {
          message: 'To be eligible for Forecasting value must be of "Warning", "Progressed", "Critical".',
        },
      },
      {
        id: 'workspace_id',
        label: 'VBL Workspace',
        type: 'select',
        value: form.workspace_id,
        required: true,
        options: workspaceOptions,
        placeholder: 'Select a VBL workspace',
        validation: {
          message: 'The workspace whose Weibull parameters will forecast this record.',
        },
      },
      {
        id: 'detected_date',
        label: 'Detection Date',
        type: 'date',
        value: form.detected_date,
        required: true,
        disabled: mode === 'edit',
      },
      {
        id: 'case_id',
        label: 'Case ID',
        type: 'text',
        value: form.case_id,
        required: true,
        placeholder: 'AI Hub case reference',
        disabled: mode === 'edit',
        validation: {
          message: 'If no case Id is available, please use "---"',
        },
      },
      { id: '__section_optional', label: 'Optional', type: 'section', value: '' },
      {
        id: 'latest_inspection_severity_id',
        label: 'Last Inspection Severity',
        type: 'select',
        value: form.latest_inspection_severity_id,
        options: severityOptions,
        placeholder: 'Select a severity',
      },
      {
        id: 'down_date',
        label: 'Down Date',
        type: 'date',
        value: form.down_date,
        validation: {
          message: 'Setting a down date removes this record from future forecasts.',
        },
      },
      {
        id: 'actual_rts_date',
        label: 'Return-to-Service Date',
        type: 'date',
        value: form.actual_rts_date,
      },
      {
        id: 'outcome_id',
        label: 'Outcome',
        type: 'select',
        value: form.outcome_id,
        options: outcomeOptions,
        placeholder: 'Select an outcome',
      },
    ];
  }, [
    filterOptionsQuery.data,
    filterMappingsQuery.data,
    turbinesForSiteQuery.data,
    vblWorkspacesQuery.data,
    form,
    mode
  ]);

  const handleFieldChange = (fieldId: string, value: string | number) => {
    const stringValue = String(value);
    setForm((prev) => {
      const next = { ...prev, [fieldId]: stringValue };
      // Clear turbine selection when site changes — the turbine list re-fetches.
      if (fieldId === 'site_id' && stringValue !== prev.site_id) {
        next.turbine_id = '';
      }
      return next;
    });
    setErrors((prev) => {
      if (!prev[fieldId]) return prev;
      const { [fieldId]: _omit, ...rest } = prev;
      return rest;
    });
  };

  const validate = (): boolean => {
    const next: Record<string, string> = {};
    if (!form.site_id) next.site_id = 'Site is required';
    if (!form.turbine_id) next.turbine_id = 'Turbine is required';
    if (!form.major_component_id) next.major_component_id = 'Major component is required';
    if (!form.component_id) next.component_id = 'Sub-component is required';
    if (!form.failure_mode_name) next.failure_mode_name = 'Failure mode is required';
    if (!form.detected_date) next.detected_date = 'Detection date is required';
    if (!form.case_id.trim()) next.case_id = 'Case ID is required';
    if (!form.latest_diagnostic_severity_id) next.latest_diagnostic_severity_id = 'Last diagnostic severity is required';
    if (!form.workspace_id) next.workspace_id = 'Workspace is required';
    setErrors(next);
    return Object.keys(next).length === 0;
  };

  const handleAPIError = (err: unknown) => {
    if (err instanceof AxiosError) {
      const data = err.response?.data;
      const parsed = typeof data === 'string' ? (JSON.parse(data) as Record<string, string>) : data;
      const message =
        (parsed && (parsed.msg || parsed.error)) || 'An error occurred saving the record.';
      setApiError(message);
    } else {
      setApiError('An unexpected error occurred saving the record.');
    }
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setIsSubmitting(true);

    const payload: NewFailurePayload = {
      failure_mode_name: form.failure_mode_name,
      detected_date: dateInputToISO(form.detected_date),
      down_date: dateInputToISO(form.down_date),
      predicted_rts_date: '0001-01-01T00:00:00Z',
      actual_rts_date: dateInputToISO(form.actual_rts_date),
      install_date: '0001-01-01T00:00:00Z',
      case_id: form.case_id,
      turbine_id: form.turbine_id,
      outcome_id: form.outcome_id || ZERO_UUID,
      component_id: form.component_id,
      major_component_id: form.major_component_id,
      major_corrective: '',
      planned_reactive: '',
      latest_inspection_severity_id: form.latest_inspection_severity_id || ZERO_UUID,
      latest_diagnostic_severity_id: form.latest_diagnostic_severity_id || ZERO_UUID,
    };

    try {
      if (mode === 'edit' && initial?.failure_id) {
        const updated: UpdatedFailurePayload = { ...payload, failure_id: initial.failure_id };
        await failuresApi.updateFailure(initial.failure_id, updated);
      } else {
        await failuresApi.createFailure(payload);
      }

      // Upsert turbine_to_workspace_config so the new/edited record is forecastable.
      // Skip the round-trip when the existing mapping already targets the chosen workspace.
      const existingMapping = findBestMappingForCombo(
        turbineWorkspaceMappingsQuery.data?.mappings ?? [],
        form.turbine_id,
        form.major_component_id,
        form.component_id,
      );
      if (!existingMapping || existingMapping.workspace_id !== form.workspace_id) {
        if (!user?.id) {
          throw new Error('User ID is required to assign a VBL workspace mapping.');
        }
        if (existingMapping) {
          await vblApi.deleteMapping(existingMapping);
        }
        await vblApi.createMapping(
          {
            turbine_id: form.turbine_id,
            workspace_id: form.workspace_id,
            major_component_id: form.major_component_id,
            component_id: form.component_id,
          },
          user.id,
        );
      }

      // Detect close-off: an edit that sets a down_date retires the record from the
      // forecast pool. We deliberately skip refreshing vbl-forecast-data so the row
      // remains visible (greyed-out) in the table and the user can undo the close-off.
      //
      // Invariant: form.down_date is reset to '' on every modal open in edit mode
      // (see the pre-populate useEffect), so any non-empty value here is necessarily
      // a value the user entered this session. If that initialisation ever changes
      // to pre-populate the existing down_date, this condition will start firing on
      // every save of an already-closed record — revisit then.
      const isClosingOff =
        mode === 'edit' && !!initial?.failure_id && !!form.down_date.trim();
      if (isClosingOff) {
        onClosedOff?.(initial!.failure_id);
      } else {
        await queryClient.invalidateQueries({ queryKey: ['vbl-forecast-data'] });
      }
      await queryClient.invalidateQueries({ queryKey: ['turbine-workspace-mappings'] });
      onSaved();
      onClose();
    } catch (err) {
      handleAPIError(err);
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <>
      <ModalCard
        isOpen={isOpen}
        onClose={onClose}
        title={mode === 'edit' ? 'Edit VBL Forecast Record' : 'New VBL Forecast Record'}
        subtitle={
          mode === 'edit'
            ? 'Update the record. Setting a down date will close the record off from future forecasts.'
            : 'Create a new record. Records without a down date appear on the forecast page.'
        }
        size="lg"
        showCloseButton
      >
        <ConfigCard
          fields={fields}
          onFieldChange={handleFieldChange}
          onSubmit={handleSubmit}
          onCancel={onClose}
          submitLabel={mode === 'edit' ? 'Save Changes' : 'Create Record'}
          isSubmitting={isSubmitting}
          errors={errors}
        />
      </ModalCard>
      {apiError && (
        <ErrorModal open={!!apiError} onClose={() => setApiError(null)} error={apiError} />
      )}
    </>
  );
};

export default VblForecastEditModal;
